import CrudActionContainer from './CrudActionContainer';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import { messengerAdsHandler } from '../errorHandlers/messanger-ads-handler';
import { MessengerAdController } from '../controllers/MessengerAdController';
import {
  selectAdConfig,
  selectMessengerAd,
  unselectMessengerAd,
} from './viewActions';
import { Dispatch } from 'redux';
import { StoreState } from '../store-interfaces';
import {
  ExtendedMessengerAdModel,
  MessengerAdModel,
} from '../model/MessengerAdModel';
import audiencesCRUDActions from './AudiencesCRUDActions';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { MessengerAdRemoteAdModel } from '../model/MessengerAdRemoteAdModel';

class MessengerAdCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('MESSENGER_ADS');
    this.resetAction = super.makeActionKey('RESET');
  }

  create(botId: number, entity: Partial<ExtendedMessengerAdModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await MessengerAdController.postMessengerAd(
          botId,
          entity,
        );
        dispatch(audiencesCRUDActions.retrieved(response.audience));
        dispatch(this.retrieved(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await MessengerAdController.getAll(botId);

        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveById(botId: number, adId: number, unbusy: boolean = true) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));
      try {
        const response = await MessengerAdController.getById(botId, adId);
        if (unbusy) {
          dispatch(this.updateLocal(response));
          dispatch(selectMessengerAd(response));
        }
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  updateLegacy(botId: number, entity: Partial<MessengerAdModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.putMessengerAd(
          botId,
          entity,
        );
        dispatch(this.updateLocal(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  prepareReplacementPage(botId: number, entityId: number, pageId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.prepareReplacementPage(
          botId,
          entityId,
          pageId,
        );
        dispatch(this.retrieved(response));
        dispatch(selectAdConfig(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  sync(
    botId: number,
    adId: number,
    body: { ads?: MessengerAdRemoteAdModel[] },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await MessengerAdController.facebookSync(botId, adId, body);
      } catch (e) {
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  update(botId: number, entity: Partial<ExtendedMessengerAdModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.putMessengerAd(
          botId,
          entity,
        );
        dispatch(this.updateLocal(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        return messengerAdsHandler(dispatch)(e);
      }
    };
  }

  updateAudienceName(
    botId: number,
    messengerAdId: number,
    audienceName: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.changeAudienceName(
          botId,
          messengerAdId,
          audienceName,
        );
        dispatch(audiencesCRUDActions.retrieved(response.audience));
        dispatch(this.updateLocal(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        return messengerAdsHandler(dispatch)(e);
      }
    };
  }

  createAudience(botId: number, messengerAdId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.createAudience(
          botId,
          messengerAdId,
        );
        dispatch(audiencesCRUDActions.retrieved(response.audience));
        dispatch(this.updateLocal(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        return messengerAdsHandler(dispatch)(e);
      }
    };
  }

  destroyAudience(botId: number, messengerAdId: number, audienceId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await MessengerAdController.destroyAudience(
          botId,
          messengerAdId,
        );
        dispatch(
          audiencesCRUDActions.deleteLocal({ removedEntityId: audienceId }),
        );
        dispatch(this.updateLocal(response));
        dispatch(selectMessengerAd(response));
        return response;
      } catch (e) {
        return messengerAdsHandler(dispatch)(e);
      }
    };
  }

  delete(botId: number, entity: Partial<MessengerAdModel> & { id: number }) {
    return async (dispatch: Dispatch<StoreState>) => {
      const { id } = entity;
      try {
        dispatch(this.busy(this.deleteLocalAction));
        dispatch(this.deleteLocal(entity));
        const response = await MessengerAdController.deleteMessengerAd(
          botId,
          id,
        );
        dispatch(this.retrieveAll(botId));
        dispatch(unselectMessengerAd());
        return response;
      } catch (e) {
        permissionsHandler(dispatch, this)(e);
      }
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }
}

const messengerAdCRUDActions = new MessengerAdCRUDActions();

export default messengerAdCRUDActions;

export const useMessengerAdCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (chatbotId: number) =>
        dispatch(messengerAdCRUDActions.retrieveAll(chatbotId)),
      create: (...props: Parameters<typeof messengerAdCRUDActions.create>) =>
        dispatch(messengerAdCRUDActions.create(...props)),
      delete: (...props: Parameters<typeof messengerAdCRUDActions.delete>) =>
        dispatch(messengerAdCRUDActions.delete(...props)),
    }),
    [dispatch],
  );
};
